import { Slot } from "@radix-ui/react-slot";
import * as React from "react";

import { cn } from "@/nucleo/utilitarios";

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("rounded-xl border bg-card text-card-foreground shadow-xs", className)} {...props} />
));
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

/**
 * Título do cartão.
 *
 * Renderiza `h3` por padrão, que é o nível certo quando o cartão é um bloco
 * dentro de uma tela que já tem `h1` e `h2`. Quando o cartão É a tela — as
 * páginas do portal público de credenciamento, por exemplo —, `asChild` deixa
 * o consumidor escolher o elemento: sem `h1` quem navega por cabeçalho entra
 * num documento que começa no terceiro nível, e todo `h2` abaixo fica
 * pendurado em nada. É o mesmo `asChild` de `Button` e `BreadcrumbLink`.
 *
 *     <CardTitle asChild className="text-lg">
 *       <h1>Retomar inscrição</h1>
 *     </CardTitle>
 */
const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "h3";
  return (
    <Comp ref={ref} className={cn("text-base font-semibold leading-none tracking-tight", className)} {...props} />
  );
});
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
